import React from 'react';

interface UseFilterSelectorKeyboardParams {
  disabled: boolean;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  searchRef: React.RefObject<HTMLInputElement | null>;
  chipWrapperRefs: React.RefObject<(HTMLButtonElement | null)[]>;
  listRef: React.RefObject<HTMLUListElement | null>;
  filtered: { key: string }[];
  handleToggleItem: (key: string) => void;
  handleToggleAll: () => void;
  handleRemoveItem: (key: string) => void;
  handleRemoveAll: () => void;
  lastFocusedIndex: number;
  setLastFocusedIndex: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Keyboard navigation handlers for the FilterSelector component.
 *
 * Extracted to keep FilterSelector focused on rendering concerns while
 * centralising all keyboard interaction logic in core/utils.
 */
export function useFilterSelectorKeyboard({
  disabled,
  isOpen,
  setIsOpen,
  setSearch,
  searchRef,
  chipWrapperRefs,
  listRef,
  filtered,
  handleToggleItem,
  handleRemoveItem,
  handleRemoveAll,
  lastFocusedIndex,
  setLastFocusedIndex,
}: UseFilterSelectorKeyboardParams) {
  const getWrappers = (): HTMLButtonElement[] =>
    (chipWrapperRefs.current.filter(Boolean) as HTMLButtonElement[]).filter((el) => el.isConnected);

  const focusChip = (index: number) => {
    const wrappers = getWrappers();
    const target = wrappers[index];
    if (target) {
      target.focus();
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Escape') { setIsOpen(false); setSearch(''); return; }
    if (e.key === 'ArrowLeft' && searchRef.current?.selectionStart === 0) {
      const wrappers = getWrappers();
      if (wrappers.length > 0) { e.preventDefault(); wrappers.at(-1)?.focus(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      // Focus the last focused button, or the first button if none was focused before
      requestAnimationFrame(() => {
        const buttons = Array.from(listRef.current?.querySelectorAll('button') ?? []);
        if (buttons.length === 0) return;
        
        const targetIndex = lastFocusedIndex >= 0 && lastFocusedIndex < buttons.length 
          ? lastFocusedIndex 
          : 0;
        
        const targetButton = buttons[targetIndex] as HTMLButtonElement | undefined;
        targetButton?.focus();
        setLastFocusedIndex(targetIndex);
      });
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen) { setIsOpen(true); return; }
    if (filtered.length > 0) handleToggleItem(filtered[0].key);
  };

  const handleChipKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    key: string,
    index: number,
    isAll: boolean,
  ) => {
    if (disabled) return;
    const wrappers = getWrappers();
    const lastIndex = wrappers.length - 1;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (index > 0) focusChip(index - 1);
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (index < lastIndex) focusChip(index + 1);
      else searchRef.current?.focus();
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      if (isAll) { handleRemoveAll(); return; }
      handleRemoveItem(key);
      requestAnimationFrame(() => {
        const updated = getWrappers();
        if (updated.length > 0) updated[Math.min(index, updated.length - 1)]?.focus();
        else searchRef.current?.focus();
      });
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      searchRef.current?.focus();
    }
  };

  return { handleTriggerKeyDown, handleChipKeyDown };
}
